import { useEffect, useMemo, useState, useCallback } from 'react';

interface FormValidation<T> {
  [key: string]: [(value: T) => boolean, string];
}

interface FormState {
  [key: string]: any;
}

interface FormTarget {
  name: string;
  value: any;
}

export interface FormChangeEvent {
  target: FormTarget;
}

export const useForm = <T extends FormState>(
  initialForm: T,
  formValidations: FormValidation<any> = {},
) => {
  const [formState, setFormState] = useState<T>(initialForm);
  const [formValidation, setFormValidation] = useState<
    Record<string, string | null>
  >({});
  const [isDirty, setIsDirty] = useState(false);

  const createValidators = useCallback(() => {
    const formCheckedValues: Record<string, string | null> = {};
    for (const formField of Object.keys(formValidations)) {
      const [fn, errorMessage] = formValidations[formField];
      formCheckedValues[`${formField}Valid`] = fn(formState[formField])
        ? null
        : errorMessage;
    }
    setFormValidation(formCheckedValues);
  }, [formState, formValidations]);

  // Only run validation when formValidations change
  useEffect(() => {
    if (Object.keys(formValidations).length > 0) {
      createValidators();
    }
  }, [formValidations]);

  const isFormValid = useMemo(() => {
    for (const formValue of Object.keys(formValidation)) {
      if (formValidation[formValue] !== null) return false;
    }
    return true;
  }, [formValidation]);

  const onInputChange = ({ target }: FormChangeEvent) => {
    if (!isDirty) setIsDirty(true);
    const { name, value } = target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Run validation immediately for the changed field if needed
    if (formValidations[name]) {
      const [fn, errorMessage] = formValidations[name];
      setFormValidation((prev) => ({
        ...prev,
        [`${name}Valid`]: fn(value) ? null : errorMessage,
      }));
    }
  };

  const onResetForm = useCallback(
    (newFormState: T = initialForm) => {
      setFormState(newFormState);
      setIsDirty(false);
      // Reset validation state when form is reset
      setFormValidation({});
    },
    [initialForm],
  );

  return {
    isFormValid,
    ...formState,
    formState,
    ...formValidation,
    onInputChange,
    onResetForm,
    isDirty,
    setIsDirty,
  };
};
